export const OCPP_MV_PARSE_STORAGE_KEY = "fcr-charger-log-parse-meter-values";

/** MV lookbacks for rolling P̄ (payload t1); n = samples back from current MV. */
export const PN_LOOKBACK_SAMPLES = [1, 2, 4, 5, 10, 12, 15, 20, 24, 30, 60] as const;

export function readMeterValuesParsePref(): boolean {
  try {
    return localStorage.getItem(OCPP_MV_PARSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeMeterValuesParsePref(enabled: boolean): void {
  try {
    localStorage.setItem(OCPP_MV_PARSE_STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

type SampledValue = {
  value?: string;
  context?: string;
  measurand?: string;
  phase?: string;
  unit?: string;
};

type MeterValueBlock = {
  timestamp?: string;
  sampledValue?: SampledValue[];
};

type MeterValuesPayload = {
  meterValue?: MeterValueBlock[];
};

type OcppCall = {
  action: string;
  payload: MeterValuesPayload;
};

type ContextGroup = {
  context: string;
  timestamp: string;
  currentImport: { value: number; unit: string }[];
  currentOffered: { value: number; unit: string } | null;
  voltage: { value: number; unit: string }[];
  power: { value: number; unit: string }[];
  energy: { value: number; unit: string } | null;
};

export type MeterValuesSnapshot = {
  payloadTsMs: number | null;
  logTsMs: number | null;
  energyKwh: number | null;
};

export type FormatMeterValuesOptions = {
  /** BCU100xxx / BCU200xxx — CSV currents, second-granularity payload ts, dual Δt. */
  bcu12: boolean;
  logTsMs: number | null;
};

/** Motown BCU gen 1/2 (CSV I/V; Power.Active.Import kW, often without unit). */
export function isBcu12ChargerId(chargerId: string): boolean {
  const id = chargerId.replace(/^\//, "").trim();
  return /^BCU[12]/i.test(id);
}

export function parseLogLineTs(line: string): number | null {
  const match = line.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z)/);
  if (!match) return null;
  const ms = Date.parse(match[1]);
  return Number.isFinite(ms) ? ms : null;
}

const PHASE_ORDER = ["L1", "L2", "L3", "N", "L1-N", "L2-N", "L3-N", "L1-L2", "L2-L3", "L3-L1"];

function phaseSortKey(phase: string | undefined): number {
  if (!phase) return 100;
  const i = PHASE_ORDER.indexOf(phase);
  return i >= 0 ? i : 50;
}

function parseNum(raw: unknown): number | null {
  if (raw == null) return null;
  const n = Number.parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

function normMeasurand(m: string | undefined): string {
  return String(m ?? "").trim();
}

function isCurrentImport(measurand: string): boolean {
  return normMeasurand(measurand) === "Current.Import";
}

function isCurrentOffered(measurand: string): boolean {
  return normMeasurand(measurand) === "Current.Offered";
}

function isVoltageMeasurand(measurand: string): boolean {
  return normMeasurand(measurand) === "Voltage";
}

function isPowerMeasurand(measurand: string): boolean {
  const m = normMeasurand(measurand);
  return m === "Power" || m.startsWith("Power.");
}

function isEnergyImportRegister(measurand: string): boolean {
  const m = normMeasurand(measurand).toLowerCase();
  return m.includes("energy") && m.includes("import");
}

function energyToKwh(value: number, unit: string | undefined): number {
  const u = String(unit ?? "kWh").trim().toLowerCase();
  if (u === "wh") return value / 1000;
  return value;
}

function fmtListValue(n: number): string {
  const s = n.toFixed(3);
  return s.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}

function fmtDeltaSeconds(sec: number): string {
  if (sec >= 100) return `${Math.round(sec)} s`;
  if (sec >= 10) return `${sec.toFixed(1)} s`;
  return `${sec.toFixed(1)} s`;
}

/** Up to 3 decimals, trailing zeros trimmed (BCU1/2 Δt1/Δt2). */
function fmtDeltaSecondsUpTo3(sec: number): string {
  const s = sec.toFixed(3).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return `${s} s`;
}

/** BCU1/2 often omit unit on Power.Active.Import; values are kW, not W. */
function powerToKw(value: number, unit: string | undefined, bcu12 = false): number {
  let u = String(unit ?? "").trim().toLowerCase();
  if (!u || u === "?") {
    u = bcu12 ? "kw" : "w";
  }
  if (u === "kw") return value;
  if (u === "w") return value / 1000;
  if (u === "mw") return value * 1000;
  return value / 1000;
}

function powerDisplayUnit(unit: string | undefined): "kW" | "W" {
  const u = String(unit ?? "W").trim().toLowerCase();
  if (u === "kw" || u === "w" || u === "mw") return "kW";
  return "kW";
}

function sumPowerKw(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0);
}

function fmtSumKw(kw: number): string {
  const abs = Math.abs(kw);
  if (abs >= 10) return `${kw.toFixed(2)} kW`;
  if (abs >= 1) return `${kw.toFixed(3)} kW`;
  return `${kw.toFixed(3)} kW`;
}

function fmtEnergyDelta(deltaKwh: number): string {
  const abs = Math.abs(deltaKwh);
  if (abs >= 1) return `${deltaKwh.toFixed(3)} kWh`;
  if (abs >= 0.001) return `${deltaKwh.toFixed(3)} kWh`;
  return `${(deltaKwh * 1000).toFixed(1)} Wh`;
}

/** ΔE (kWh) / Δt (s) → kW using full timestamp precision. */
function avgPowerKwFromDelta(deltaKwh: number, deltaSec: number): number | null {
  if (!Number.isFinite(deltaSec) || deltaSec <= 0) return null;
  return (deltaKwh * 3600) / deltaSec;
}

function fmtAvgPowerKw(kw: number): string {
  return `${kw.toFixed(3)} kW`;
}

function parseCsvNumbers(valueStr: string): number[] {
  return valueStr
    .split(",")
    .map((part) => parseNum(part.trim()))
    .filter((n): n is number => n != null);
}

function mvHeaderLine(context: string, tsLabel: string, bcu12: boolean): string {
  if (bcu12 && (!context || context === "Unknown")) {
    return `MV @ ${tsLabel}`;
  }
  return `MV, ${context} @ ${tsLabel}`;
}

function parseOcppCallBody(body: string): OcppCall | null {
  const trimmed = body.trim();
  if (!trimmed.startsWith("[")) return null;
  try {
    const arr = JSON.parse(trimmed) as unknown[];
    if (!Array.isArray(arr) || arr.length < 3 || arr[0] !== 2) return null;
    const action = String(arr[2] ?? "");
    if (action !== "MeterValues") return null;
    const payload = (arr[3] ?? {}) as MeterValuesPayload;
    return { action, payload };
  } catch {
    return null;
  }
}

function sortPhaseSamples<T extends { phase?: string; order: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const pa = phaseSortKey(a.phase);
    const pb = phaseSortKey(b.phase);
    if (pa !== pb) return pa - pb;
    return a.order - b.order;
  });
}

function groupPayloadByContext(payload: MeterValuesPayload): ContextGroup[] {
  const meterValues = payload.meterValue ?? [];
  const groups: ContextGroup[] = [];

  for (const block of meterValues) {
    const timestamp = block.timestamp ?? "";
    const sampled = block.sampledValue ?? [];
    const byContext = new Map<string, SampledValue[]>();

    for (const item of sampled) {
      const ctx = item.context?.trim() || "Unknown";
      const list = byContext.get(ctx) ?? [];
      list.push(item);
      byContext.set(ctx, list);
    }

    for (const [context, items] of byContext) {
      const currentImport: { value: number; unit: string; phase?: string; order: number }[] = [];
      let currentOffered: { value: number; unit: string } | null = null;
      const voltage: { value: number; unit: string; phase?: string; order: number }[] = [];
      const power: { value: number; unit: string; phase?: string; order: number }[] = [];
      let energy: { value: number; unit: string } | null = null;

      items.forEach((item, order) => {
        const measurand = normMeasurand(item.measurand);
        const valueStr = item.value != null ? String(item.value) : "";
        const unit = String(item.unit ?? "").trim() || "?";

        if (isCurrentImport(measurand)) {
          if (valueStr.includes(",")) {
            parseCsvNumbers(valueStr).forEach((val, idx) => {
              currentImport.push({
                value: val,
                unit: unit === "?" ? "A" : unit,
                order: idx,
              });
            });
          } else {
            const val = parseNum(valueStr);
            if (val != null) {
              currentImport.push({ value: val, unit: unit === "?" ? "A" : unit, phase: item.phase, order });
            }
          }
          return;
        }

        if (isVoltageMeasurand(measurand)) {
          if (valueStr.includes(",")) {
            parseCsvNumbers(valueStr).forEach((val, idx) => {
              voltage.push({
                value: val,
                unit: unit === "?" ? "V" : unit,
                order: idx,
              });
            });
          } else {
            const val = parseNum(valueStr);
            if (val != null) {
              voltage.push({ value: val, unit: unit === "?" ? "V" : unit, phase: item.phase, order });
            }
          }
          return;
        }

        const val = parseNum(valueStr);
        if (val == null) return;

        if (isCurrentOffered(measurand)) {
          currentOffered = { value: val, unit: unit === "?" ? "A" : unit };
        } else if (isPowerMeasurand(measurand)) {
          if (valueStr.includes(",")) {
            parseCsvNumbers(valueStr).forEach((pVal, idx) => {
              power.push({ value: pVal, unit, order: idx });
            });
          } else {
            power.push({ value: val, unit, phase: item.phase, order });
          }
        } else if (isEnergyImportRegister(measurand)) {
          energy = { value: val, unit: unit === "?" ? "kWh" : unit };
        }
      });

      groups.push({
        context,
        timestamp,
        currentImport: sortPhaseSamples(currentImport),
        currentOffered,
        voltage: sortPhaseSamples(voltage),
        power: sortPhaseSamples(power),
        energy,
      });
    }
  }

  return groups;
}

function formatContextBlock(
  group: ContextGroup,
  prev: MeterValuesSnapshot | null | undefined,
  opts: FormatMeterValuesOptions,
): string {
  const lines: string[] = [];
  const tsLabel = group.timestamp || "—";
  lines.push(mvHeaderLine(group.context, tsLabel, opts.bcu12));

  if (group.currentImport.length > 0) {
    const unit = group.currentImport[0]?.unit ?? "A";
    const vals = group.currentImport.map((c) => fmtListValue(c.value)).join(", ");
    let iLine = `I: {${vals}} ${unit}`;
    if (group.currentOffered) {
      iLine += `, offered ${fmtListValue(group.currentOffered.value)} ${group.currentOffered.unit}`;
    }
    lines.push(iLine);
  } else if (group.currentOffered) {
    lines.push(
      `I: offered ${fmtListValue(group.currentOffered.value)} ${group.currentOffered.unit}`,
    );
  }

  if (group.voltage.length > 0) {
    const unit = group.voltage[0]?.unit ?? "V";
    const vals = group.voltage.map((v) => fmtListValue(v.value)).join(", ");
    lines.push(`V: {${vals}} ${unit}`);
  }

  if (group.power.length > 0) {
    const rawUnit = group.power[0]?.unit ?? "W";
    const displayUnit = powerDisplayUnit(rawUnit);
    const kwValues = group.power.map((p) => powerToKw(p.value, p.unit, opts.bcu12));
    const vals = kwValues.map((v) => fmtListValue(v)).join(", ");
    let pLine = `P: [${vals}] ${displayUnit}`;
    const sumKw = sumPowerKw(kwValues);
    if (sumKw != null) {
      pLine += `, \u03A3 ${fmtSumKw(sumKw)}`;
    }
    lines.push(pLine);
  }

  if (group.energy) {
    const eUnit = group.energy.unit;
    const eKwh = energyToKwh(group.energy.value, eUnit);
    const eDisplay =
      eUnit.toLowerCase() === "wh"
        ? `${group.energy.value} Wh`
        : `${fmtListValue(group.energy.value)} ${eUnit}`;

    let eLine = `E: ${eDisplay}`;
    const payloadTsMs = group.timestamp ? Date.parse(group.timestamp) : null;
    const prevEnergy = prev?.energyKwh ?? null;
    const prevTsMs = prev?.payloadTsMs ?? null;

    if (prevEnergy != null && prevTsMs != null && payloadTsMs != null && Number.isFinite(payloadTsMs)) {
      const deltaKwh = eKwh - prevEnergy;
      const deltaSec1 = (payloadTsMs - prevTsMs) / 1000;
      eLine += `, ΔE ${fmtEnergyDelta(deltaKwh)}`;
      if (opts.bcu12) {
        eLine += `, Δt1 ${fmtDeltaSecondsUpTo3(deltaSec1)}`;
        const prevLogMs = prev?.logTsMs ?? null;
        let deltaSec2: number | null = null;
        if (prevLogMs != null && opts.logTsMs != null && Number.isFinite(opts.logTsMs)) {
          deltaSec2 = (opts.logTsMs - prevLogMs) / 1000;
          eLine += `, Δt2 ${fmtDeltaSecondsUpTo3(deltaSec2)}`;
        } else {
          eLine += `, Δt2 —`;
        }
        const pAvg1 = avgPowerKwFromDelta(deltaKwh, deltaSec1);
        if (pAvg1 != null) {
          eLine += `, P\u03041 ${fmtAvgPowerKw(pAvg1)}`;
        }
        if (deltaSec2 != null) {
          const pAvg2 = avgPowerKwFromDelta(deltaKwh, deltaSec2);
          if (pAvg2 != null) {
            eLine += `, P\u03042 ${fmtAvgPowerKw(pAvg2)}`;
          }
        } else {
          eLine += `, P\u03042 —`;
        }
      } else {
        eLine += `, Δt ${fmtDeltaSeconds(deltaSec1)}`;
        const pAvgKw = avgPowerKwFromDelta(deltaKwh, deltaSec1);
        if (pAvgKw != null) {
          eLine += `, P\u0304 ${fmtAvgPowerKw(pAvgKw)}`;
        }
      }
    } else {
      eLine += opts.bcu12 ? `, ΔE —, Δt1 —, Δt2 —, P\u03041 —, P\u03042 —` : `, ΔE —, Δt —`;
    }
    lines.push(eLine);
  }

  return lines.join("\n");
}

export function extractMeterValuesSnapshot(body: string): MeterValuesSnapshot | null {
  const call = parseOcppCallBody(body);
  if (!call) return null;

  let payloadTsMs: number | null = null;
  let energyKwh: number | null = null;

  for (const block of call.payload.meterValue ?? []) {
    if (block.timestamp) {
      const ms = Date.parse(block.timestamp);
      if (Number.isFinite(ms)) payloadTsMs = ms;
    }
    for (const item of block.sampledValue ?? []) {
      if (!isEnergyImportRegister(normMeasurand(item.measurand))) continue;
      const val = parseNum(item.value);
      if (val == null) continue;
      energyKwh = energyToKwh(val, item.unit);
    }
  }

  return { payloadTsMs, logTsMs: null, energyKwh };
}

function extractMeterValuesSnapshotFromLine(line: string): MeterValuesSnapshot | null {
  const body = splitLogBody(line);
  if (!body) return null;
  const snap = extractMeterValuesSnapshot(body);
  if (!snap) return null;
  return { ...snap, logTsMs: parseLogLineTs(line) };
}

/** Build map lineIndex → chronologically previous MeterValues snapshot (lines newest-first). */
export function buildPreviousMeterValuesMap(lines: string[]): Map<number, MeterValuesSnapshot> {
  const prevByIndex = new Map<number, MeterValuesSnapshot>();
  let last: MeterValuesSnapshot | null = null;

  for (let i = lines.length - 1; i >= 0; i--) {
    const snap = extractMeterValuesSnapshotFromLine(lines[i]!);
    if (!snap) continue;
    if (last) prevByIndex.set(i, last);
    last = snap;
  }

  return prevByIndex;
}

export type MeterValuesIndexEntry = {
  lineIndex: number;
  snap: MeterValuesSnapshot;
};

/** MV lines only, newest-first (matches log line order). */
export function buildMeterValuesIndexList(lines: string[]): MeterValuesIndexEntry[] {
  const list: MeterValuesIndexEntry[] = [];
  for (let i = 0; i < lines.length; i++) {
    const snap = extractMeterValuesSnapshotFromLine(lines[i]!);
    if (!snap) continue;
    if (snap.energyKwh == null || snap.payloadTsMs == null) continue;
    list.push({ lineIndex: i, snap });
  }
  return list;
}

/**
 * Rolling average P from payload energy/timestamp (t1) over n MV samples back.
 * One line: Pn: 1=6.480, 2=6.451, … kW (omits n when insufficient history).
 */
export function formatPnLine(lineIndex: number, mvList: MeterValuesIndexEntry[]): string | null {
  const p = mvList.findIndex((m) => m.lineIndex === lineIndex);
  if (p < 0) return null;

  const parts: string[] = [];
  for (const n of PN_LOOKBACK_SAMPLES) {
    const oldestPos = p + n;
    if (oldestPos >= mvList.length) continue;

    const newest = mvList[p]!.snap;
    const oldest = mvList[oldestPos]!.snap;
    if (newest.energyKwh == null || oldest.energyKwh == null) continue;
    if (newest.payloadTsMs == null || oldest.payloadTsMs == null) continue;

    const deltaKwh = newest.energyKwh - oldest.energyKwh;
    const deltaSec = (newest.payloadTsMs - oldest.payloadTsMs) / 1000;
    const pKw = avgPowerKwFromDelta(deltaKwh, deltaSec);
    if (pKw == null) continue;

    parts.push(`${n}=${pKw.toFixed(3)}`);
  }

  if (!parts.length) return null;
  return `Pn: ${parts.join(", ")} kW`;
}

function splitLogBody(line: string): string | null {
  const inIdx = line.indexOf(" in ");
  const outIdx = line.indexOf(" out ");
  const dirIdx = inIdx >= 0 ? inIdx : outIdx;
  if (dirIdx < 0) return null;
  const dir = inIdx >= 0 ? "in" : "out";
  return line.slice(dirIdx + dir.length + 2);
}

/**
 * Replace MeterValues JSON body with parsed summary (version 3 + offered on I line).
 * Returns null if not a parseable MeterValues call.
 */
export function formatMeterValuesLogBody(
  body: string,
  prev: MeterValuesSnapshot | null | undefined,
  opts: FormatMeterValuesOptions,
): string | null {
  if (body.endsWith("…") || body.endsWith("...")) return null;
  const call = parseOcppCallBody(body);
  if (!call) return null;

  const groups = groupPayloadByContext(call.payload);
  if (!groups.length) return null;

  return groups.map((g) => formatContextBlock(g, prev, opts)).join("\n\n");
}

